/**
 * @file Wave generator debug window (issue #26). One tab of the developer
 * window, which the `*` key opens (issue #56).
 *
 * A balancing sandbox for the wave director: it shows what
 * {@link ../systems/WaveSystem.js WaveSystem} is doing right now, every knob
 * {@link ../config/Waves.js} exposes, and the normalised pick share of each
 * enemy template — then lets all of it be edited live, saved to `localStorage`
 * and exported as JSON.
 *
 * Like the HUD it is DOM rather than in-scene, but it is a separate component:
 * the HUD is player-facing and always on, this is developer-facing, built on
 * first open and inert while closed.
 *
 * Extending it is a config change, not a UI change — the wave rows come from
 * {@link ../config/WaveTuning.js inspectWaveTuning} and the player rows from
 * {@link ../config/PlayerTuning.js inspectPlayerTuning}, each derived from its
 * own config. A new archetype, a new numeric wave parameter, or a new numeric
 * `CONFIG.player` field appears here with no edit to this file.
 */

import { WaveSystem } from '../systems/WaveSystem.js';
import {
  exportWaveTuning,
  getFieldMeta,
  hasWaveOverrides,
  inspectWaveTuning,
  isWaveValueOverridden,
  resetWaveTuning,
  saveWaveTuning,
  setWaveValue
} from '../config/WaveTuning.js';
import {
  exportPlayerTuning,
  hasPlayerOverrides,
  inspectPlayerTuning,
  resetPlayerTuning,
  savePlayerTuning,
  setPlayerValue
} from '../config/PlayerTuning.js';

/** Seconds between refreshes while open. The panel is a readout, not a HUD. */
const REFRESH_INTERVAL = 0.2;

/** Human-readable director phases. */
const PHASE_LABELS = Object.freeze({
  preparing: 'Preparing',
  spawning: 'Spawning',
  clearing: 'Clearing'
});

export class WaveDebugPanel {
  /**
   * @param {HTMLElement} root Container element; the panel appends itself to it.
   * @param {object} [options] Presentation.
   * @param {boolean} [options.chrome] Draw the title bar. False when the panel
   *   is docked inside the debug window, which supplies its own (issue #56).
   */
  constructor(root, { chrome = true } = {}) {
    /** @type {HTMLElement} @private */
    this._root = root;
    /** @type {boolean} @private */
    this._chrome = chrome;
    /** @type {HTMLElement | null} Built on first open. @private */
    this._el = null;
    /** @type {Record<string, HTMLElement>} @private */
    this._parts = {};
    /** @type {Map<string, HTMLInputElement>} Field path -> editor. @private */
    this._inputs = new Map();
    /** @type {Map<string, HTMLInputElement>} Player field path -> editor. @private */
    this._playerInputs = new Map();
    /** @type {boolean} Whether the player grid has been built. @private */
    this._playerBuilt = false;
    /** @type {string} Archetype set the table was built for. @private */
    this._builtFor = '';
    /** @type {boolean} @private */
    this._open = false;
    /** @type {number} @private */
    this._sinceRefresh = 0;
    /** @type {number} Seconds left on the status line. @private */
    this._statusTimer = 0;
  }

  /** @returns {boolean} True while the window is on screen. */
  get isOpen() {
    return this._open;
  }

  /**
   * Shows or hides the window.
   * @returns {void}
   */
  toggle() {
    this._open ? this.close() : this.open();
  }

  /** @returns {void} */
  open() {
    if (!this._el) this._build();
    this._open = true;
    this._el.hidden = false;
    this._sinceRefresh = REFRESH_INTERVAL;
  }

  /** @returns {void} */
  close() {
    this._open = false;
    if (this._el) this._el.hidden = true;
  }

  /**
   * Per-frame tick. Returns immediately while closed, so a shipped-but-unused
   * panel costs one boolean test per frame.
   *
   * @param {number} dt Delta time in seconds.
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   */
  update(dt, ctx) {
    if (!this._open) return;

    if (this._statusTimer > 0) {
      this._statusTimer -= dt;
      if (this._statusTimer <= 0) this._parts.status.textContent = '';
    }

    this._sinceRefresh += dt;
    if (this._sinceRefresh < REFRESH_INTERVAL) return;
    this._sinceRefresh = 0;
    this._refresh(ctx);
  }

  /** @returns {void} @private */
  _build() {
    const panel = document.createElement('div');
    panel.className = this._chrome ? 'wave-debug' : 'wave-debug wave-debug--docked';
    panel.hidden = true;
    panel.innerHTML = `
      ${
        this._chrome
          ? `<header class="wave-debug__head">
        <span class="wave-debug__title">Balance sandbox</span>
        <span class="wave-debug__key"><kbd>,</kbd></span>
        <button type="button" class="wave-debug__close" data-wd="close" title="Close">×</button>
      </header>`
          : ''
      }

      <div class="wave-debug__body">
        <section class="wave-debug__section">
          <h4 class="wave-debug__heading">Director</h4>
          <div class="wave-debug__stats">
            <div><span>Phase</span><b data-wd="phase">—</b></div>
            <div><span>Wave</span><b data-wd="wave">—</b></div>
            <div><span>Floor</span><b data-wd="floor">—</b></div>
            <div><span>Budget</span><b data-wd="budget">—</b></div>
            <div><span>Queued</span><b data-wd="queued">—</b></div>
          </div>
        </section>

        <section class="wave-debug__section">
          <h4 class="wave-debug__heading">Budget &amp; pacing</h4>
          <div class="wave-debug__grid" data-wd="scalars"></div>
        </section>

        <section class="wave-debug__section">
          <h4 class="wave-debug__heading">Player</h4>
          <div class="wave-debug__grid" data-wd="player"></div>
        </section>

        <section class="wave-debug__section">
          <h4 class="wave-debug__heading">Enemy templates</h4>
          <table class="wave-debug__table">
            <thead>
              <tr>
                <th>Template</th><th>Cost</th><th>Unlock</th>
                <th>Weight</th><th>Share</th><th>Rolled</th>
              </tr>
            </thead>
            <tbody data-wd="rows"></tbody>
          </table>
          <p class="wave-debug__note" data-wd="composition">No wave rolled yet.</p>
        </section>

        <footer class="wave-debug__foot">
          <button type="button" data-wd="save">Save</button>
          <button type="button" data-wd="reset">Reset</button>
          <button type="button" data-wd="export">Export</button>
          <span class="wave-debug__status" data-wd="status"></span>
        </footer>
        <textarea class="wave-debug__json" data-wd="json" rows="8" readonly hidden></textarea>
      </div>
    `;

    this._root.appendChild(panel);
    this._el = panel;

    for (const node of panel.querySelectorAll('[data-wd]')) {
      this._parts[/** @type {HTMLElement} */ (node).dataset.wd] = /** @type {HTMLElement} */ (node);
    }

    // One delegated listener rather than one per input: rows are rebuilt
    // whenever the archetype set changes.
    panel.addEventListener('input', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      const path = input.dataset?.path;
      if (!path) return;
      const commit = input.dataset.scope === 'player' ? setPlayerValue : setWaveValue;
      if (commit(path, input.value)) {
        this._setStatus(`${path} → ${Number(input.value)} (live, unsaved)`);
      }
    });

    // Enter commits and hands the keyboard back to the game.
    panel.addEventListener('keydown', (event) => {
      const key = /** @type {KeyboardEvent} */ (event).key;
      if (key === 'Enter') /** @type {HTMLElement} */ (event.target).blur?.();
    });

    this._parts.close?.addEventListener('click', () => this.close());
    // The footer acts on both tuning layers at once: one Save/Reset/Export for
    // the whole sandbox rather than a duplicate set of buttons per section.
    this._parts.save.addEventListener('click', () => {
      const saved = [saveWaveTuning(), savePlayerTuning()].every(Boolean);
      this._setStatus(saved ? 'Saved — survives a reload.' : 'No storage available.');
    });
    this._parts.reset.addEventListener('click', () => {
      resetWaveTuning();
      resetPlayerTuning();
      this._setStatus('Reset to code defaults.');
      this._sinceRefresh = REFRESH_INTERVAL;
    });
    this._parts.export.addEventListener('click', () => {
      const json = /** @type {HTMLTextAreaElement} */ (this._parts.json);
      json.hidden = !json.hidden;
      if (json.hidden) return;
      // One pasteable object holding both tuning layers: the wave payload keeps
      // its `overrides`/`waves` shape, the player payload rides alongside it.
      const waves = JSON.parse(exportWaveTuning());
      const player = JSON.parse(exportPlayerTuning());
      json.value = JSON.stringify(
        {
          overrides: waves.overrides,
          waves: waves.waves,
          playerOverrides: player.overrides,
          player: player.player
        },
        null,
        2
      );
      json.focus();
      json.select();
    });
  }

  /**
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   * @private
   */
  _refresh(ctx) {
    const director = ctx.world.getSystem(WaveSystem) ?? null;
    const wave = director?.wave ?? 1;
    const levelIndex = director?.levelIndex ?? Math.max(1, ctx.levelIndex ?? 1);
    const snapshot = inspectWaveTuning(wave, levelIndex);

    this._parts.phase.textContent = director ? PHASE_LABELS[director.phase] ?? director.phase : '—';
    this._parts.wave.textContent = String(wave);
    this._parts.floor.textContent = String(levelIndex);
    this._parts.budget.textContent = snapshot.budget.toFixed(1);
    this._parts.queued.textContent = director
      ? `${director.rosterRemaining} + ${director.pendingCount}`
      : '—';

    this._renderScalars(snapshot.scalars);
    this._renderPlayerScalars(inspectPlayerTuning().scalars);
    this._renderArchetypes(snapshot.archetypes, director?.lastComposition ?? []);
  }

  /**
   * @param {ReturnType<typeof inspectPlayerTuning>['scalars']} scalars Numeric player fields.
   * @returns {void}
   * @private
   */
  _renderPlayerScalars(scalars) {
    // Built once: unlike the wave archetype table, the player field set does not
    // change at runtime, so a single build then value-only updates thereafter.
    if (!this._playerBuilt) {
      this._parts.player.innerHTML = scalars
        .map(
          (field) => `
            <label class="wave-debug__field">
              <span>${field.label}</span>
              <input type="number" data-path="${field.path}" data-scope="player"
                     step="${field.step}" min="${field.min}" />
            </label>`
        )
        .join('');
      for (const node of this._parts.player.querySelectorAll('input[data-path]')) {
        const input = /** @type {HTMLInputElement} */ (node);
        this._playerInputs.set(input.dataset.path, input);
      }
      this._playerBuilt = true;
    }

    for (const field of scalars) {
      this._setInput(field.path, field.value, field.overridden, this._playerInputs);
    }
  }

  /**
   * @param {ReturnType<typeof inspectWaveTuning>['scalars']} scalars Numeric wave fields.
   * @returns {void}
   * @private
   */
  _renderScalars(scalars) {
    if (this._parts.scalars.childElementCount !== scalars.length) {
      this._parts.scalars.innerHTML = scalars
        .map(
          (field) => `
            <label class="wave-debug__field">
              <span>${field.label}</span>
              <input type="number" data-path="${field.path}"
                     step="${field.step}" min="${field.min}" />
            </label>`
        )
        .join('');
      this._indexInputs();
    }

    for (const field of scalars) {
      this._setInput(field.path, field.value, field.overridden);
    }
  }

  /**
   * @param {ReturnType<typeof inspectWaveTuning>['archetypes']} archetypes Template rows.
   * @param {readonly string[]} composition Last rolled wave.
   * @returns {void}
   * @private
   */
  _renderArchetypes(archetypes, composition) {
    const signature = archetypes.map((row) => row.key).join(',');
    if (signature !== this._builtFor) {
      this._parts.rows.innerHTML = archetypes.map((row) => this._rowHtml(row)).join('');
      this._builtFor = signature;
      this._indexInputs();
    }

    /** @type {Map<string, number>} */
    const counts = new Map();
    for (const key of composition) counts.set(key, (counts.get(key) ?? 0) + 1);

    for (const row of archetypes) {
      const tr = /** @type {HTMLElement} */ (
        this._parts.rows.querySelector(`[data-row="${row.key}"]`)
      );
      if (!tr) continue;

      tr.classList.toggle('wave-debug__row--locked', !row.unlocked);
      for (const table of ['costs', 'unlocks', 'weights']) {
        const path = `${table}.${row.key}`;
        this._setInput(path, row[FIELD_OF[table]], isWaveValueOverridden(path));
      }

      const rolled = counts.get(row.key) ?? 0;
      const actual = composition.length > 0 ? rolled / composition.length : 0;
      tr.querySelector('[data-cell="share"]').textContent = row.unlocked
        ? `${(row.share * 100).toFixed(1)}%`
        : 'locked';
      tr.querySelector('[data-cell="rolled"]').textContent =
        composition.length > 0 ? `${rolled} · ${(actual * 100).toFixed(0)}%` : '—';
    }

    this._parts.composition.textContent =
      composition.length > 0
        ? `Last roll: ${composition.length} enemies — ${summarise(composition)}`
        : 'No wave rolled yet.';
  }

  /**
   * @param {ReturnType<typeof inspectWaveTuning>['archetypes'][number]} row Template row.
   * @returns {string} One table row of HTML.
   * @private
   */
  _rowHtml(row) {
    const cell = (table) => {
      const meta = getFieldMeta(`${table}.${row.key}`);
      return `<td><input type="number" data-path="${table}.${row.key}"
                         step="${meta.step}" min="${meta.min}" /></td>`;
    };
    return `
      <tr data-row="${row.key}">
        <th scope="row">${row.name}</th>
        ${cell('costs')}${cell('unlocks')}${cell('weights')}
        <td data-cell="share">—</td>
        <td data-cell="rolled">—</td>
      </tr>`;
  }

  /** @returns {void} Re-reads the wave inputs after a rebuild. @private */
  _indexInputs() {
    this._inputs.clear();
    // The player grid is indexed separately, so it is excluded here to keep the
    // two path namespaces from colliding.
    for (const node of this._el.querySelectorAll('input[data-path]:not([data-scope="player"])')) {
      const input = /** @type {HTMLInputElement} */ (node);
      this._inputs.set(input.dataset.path, input);
    }
  }

  /**
   * Writes a value into an editor, unless it is the one being typed in.
   *
   * @param {string} path Field path.
   * @param {number} value Current value.
   * @param {boolean} [overridden] Whether it differs from the shipped balance.
   * @param {Map<string, HTMLInputElement>} [map] Editor map to look in.
   * @returns {void}
   * @private
   */
  _setInput(path, value, overridden, map = this._inputs) {
    const input = map.get(path);
    if (!input || input === document.activeElement) return;

    const text = String(round(value));
    if (input.value !== text) input.value = text;
    if (overridden !== undefined) input.classList.toggle('is-overridden', overridden);
  }

  /**
   * @param {string} message Status line copy.
   * @returns {void}
   * @private
   */
  _setStatus(message) {
    this._parts.status.textContent = message;
    this._parts.status.classList.toggle('is-dirty', hasWaveOverrides() || hasPlayerOverrides());
    this._statusTimer = 3;
  }

  /** @returns {void} Removes the window and its listeners from the document. */
  dispose() {
    this._el?.remove();
    this._el = null;
    this._parts = {};
    this._inputs.clear();
    this._playerInputs.clear();
    this._playerBuilt = false;
    this._builtFor = '';
    this._open = false;
  }
}

/** Maps a `WAVES` sub-table to the field {@link inspectWaveTuning} reports it as. */
const FIELD_OF = Object.freeze({ costs: 'cost', unlocks: 'unlock', weights: 'weight' });

/**
 * @param {number} value Raw number.
 * @returns {number} Trimmed to something an input can show without noise.
 */
function round(value) {
  return Math.round(value * 1000) / 1000;
}

/**
 * @param {readonly string[]} composition Rolled archetype keys.
 * @returns {string} `grunt ×6, runner ×2`.
 */
function summarise(composition) {
  /** @type {Map<string, number>} */
  const counts = new Map();
  for (const key of composition) counts.set(key, (counts.get(key) ?? 0) + 1);
  return [...counts.entries()].map(([key, count]) => `${key} ×${count}`).join(', ');
}
